import { withWindow } from '@shared/utils/withWindow';

/**
 * Decodes HTML entities (e.g. &lt; → <) that some backend descriptions arrive with.
 * Returns the encoded string unchanged during SSR (no DOM available).
 */
export function decodeHtmlEntities(html: string): string {
  return withWindow(({ document }) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }, html);
}

function withParsedHtml<T>(html: string, domFn: (div: HTMLDivElement) => T, ssrFallback: T): T {
  return withWindow(({ document }) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return domFn(div);
  }, ssrFallback);
}

/**
 * Returns `true` when the string looks like rich-text editor output —
 * i.e. parsing it produces an element as the very first child node
 * (ignoring leading whitespace). Plain text like "Meet at <Main Gate>"
 * starts with a non-empty text node and returns `false`.
 *
 * Returns `false` during SSR (no DOM available).
 */
export function isRichTextContent(text: string): boolean {
  return withParsedHtml(
    text,
    div => {
      const firstNode = div.firstChild;
      if (firstNode === null) return false;
      if (firstNode.nodeType === Node.ELEMENT_NODE) return true;
      // Leading whitespace text nodes (e.g. "  <p>Hello</p>") are not meaningful;
      // skip them and check whether an actual element follows.
      if (firstNode.nodeType === Node.TEXT_NODE && firstNode.textContent?.trim() === '') {
        return div.firstElementChild !== null;
      }
      return false;
    },
    false,
  );
}

export function getFirstHtmlElement(htmlString: string, selector?: 'h1, h2, h3, h4, h5, h6' | 'p' | 'strong'): string | null {
  return withParsedHtml(
    htmlString,
    div => {
      const el = div.querySelector(selector ?? 'h1, h2, h3, h4, h5, h6');
      return el ? el.outerHTML : null;
    },
    null,
  );
}

/**
 * Extract plain text from content that may be HTML, suitable for card previews and list items.
 * Plain text is returned as-is. Returns `''` during SSR (no DOM available).
 */
export function getPlainTextPreview(text: string): string {
  if (!text || !isRichTextContent(text)) return text;
  return withParsedHtml(
    text,
    div => {
      const firstBlock = div.querySelector('p, h1, h2, h3, h4, h5, h6, div, li');
      return firstBlock?.textContent?.trim() ?? div.textContent?.trim() ?? '';
    },
    '',
  );
}

/**
 * If the string is already rich-text HTML, return it as-is.
 * Otherwise treat it as plain text: convert `\n` to `<br>` and wrap in a `<p>` so it
 * renders correctly in both Jodit and guest views. Uses the DOM API for safe escaping.
 * Returns the original string unchanged during SSR (no DOM available).
 */
export function ensureHtml(text: string): string {
  if (!text || isRichTextContent(text)) return text;
  return withWindow(({ document }) => {
    const p = document.createElement('p');
    text.split('\n').forEach((line, i) => {
      if (i > 0) p.appendChild(document.createElement('br'));
      p.appendChild(document.createTextNode(line));
    });
    return p.outerHTML;
  }, text);
}

/**
 * Rewrites `h1`..`h6` as paragraphs, keeping their content and dropping their attributes.
 *
 * For surfaces that don't offer a block-style control and whose renderer doesn't style headings:
 * removing the toolbar entry point stops new headings being authored, but says nothing about the
 * ones a saved draft or template already contains — and with no control left, the host can't turn
 * those back into paragraphs by hand. Normalizing on the way in is what actually delivers "this
 * surface has no headings", and it matches what the reader already sees, since the delivered email
 * ships no heading rules.
 *
 * Returns the input unchanged during SSR (no DOM available).
 */
export function flattenHeadings(html: string): string {
  if (!html.trim()) return html;
  return withParsedHtml(
    html,
    div => {
      const headings = div.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (headings.length === 0) return html;
      headings.forEach(heading => {
        const paragraph = div.ownerDocument.createElement('p');
        while (heading.firstChild) paragraph.appendChild(heading.firstChild);
        heading.replaceWith(paragraph);
      });
      return div.innerHTML;
    },
    html,
  );
}

/**
 * Strip HTML tags and return plain text. Used when feeding rich-text content
 * to plain-text consumers (e.g. calendar export). Returns `''` during SSR (no DOM available).
 */
export function stripHtmlToPlainText(html: string): string {
  if (!html.trim()) return html;

  const blockSelector = 'p, h1, h2, h3, h4, h5, h6, div, li, blockquote, tr, ul, ol, table, header, footer, section, article, aside, nav, main, figure, details';

  return withParsedHtml(
    html,
    div => {
      div.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
      div.querySelectorAll('li').forEach(li => {
        li.prepend('• ');
        li.append('\n');
      });
      div.querySelectorAll(blockSelector).forEach(el => {
        if (!el.textContent?.endsWith('\n')) el.append('\n');
      });
      // Nested blocks (e.g. <ul><li>…</li></ul>) each append a \n,
      // producing 3+ consecutive newlines — cap at two for readability.
      return (div.textContent ?? '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    },
    '',
  );
}

const LINE_BOUNDARY_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'HR', 'BR']);
const isBlock = (node: Node | null) => !!node && (node.nodeName === 'BODY' || LINE_BOUNDARY_TAGS.has(node.nodeName));
// The edge of a block counts as a boundary; the edge of an inline element (`<strong> </strong>`) does not.
const isLineBoundary = (sibling: Node | null, parent: Node | null) => (sibling ? LINE_BOUNDARY_TAGS.has(sibling.nodeName) : isBlock(parent));

const PRESERVED_WHITE_SPACE = /white-space\s*:\s*(pre|break-spaces)/i;
// Inside `<pre>` or an inline `white-space: pre*`, source whitespace renders, so it counts.
const isWhitespacePreserved = (node: Node) => {
  for (let element = node.parentElement; element; element = element.parentElement) {
    if (element.nodeName === 'PRE' || PRESERVED_WHITE_SPACE.test(element.getAttribute('style') ?? '')) return true;
  }
  return false;
};
const collapseWhitespace = (text: string) => text.replace(/[ \t\n\r\f]+/g, ' ');

// One-entry cache: the form counter and the schema both ask for the same description on every keystroke.
let lastVisibleText: Readonly<{ html: string; text: string }> | null = null;

/**
 * The text a reader sees in rich-text HTML, for counting characters: tags dropped, entities decoded, and source
 * whitespace collapsed as the browser renders it (indentation between blocks is not text).
 * DOMParser, unlike innerHTML, loads no images and runs no handlers. Without a DOM it returns the HTML unchanged.
 */
export function getVisibleText(html: string): string {
  if (!html) return '';
  if (lastVisibleText?.html === html) return lastVisibleText.text;
  const text = withWindow<string | null>(() => {
    const { body } = new DOMParser().parseFromString(html, 'text/html');
    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let visible = '';
    let collapsible = '';
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const value = node.textContent ?? '';
      if (isWhitespacePreserved(node)) {
        visible += collapseWhitespace(collapsible) + value;
        collapsible = '';
      } else if (!(/^[ \t\n\r\f]*$/.test(value) && (isLineBoundary(node.previousSibling, node.parentNode) || isLineBoundary(node.nextSibling, node.parentNode)))) {
        collapsible += value;
      }
    }
    return visible + collapseWhitespace(collapsible);
  }, null);
  // No DOM (server render): count the markup itself, which can only run high, so a limit never fails open.
  if (text === null) return html;
  lastVisibleText = { html, text };
  return text;
}
